import { Component, Output, EventEmitter, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MessageCenterService } from '@k12/messaging';
import { CreateTopicPayload } from '../message-center.model';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MessageCenterCategory, MessageCenterChildCategory } from '@k12/models';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,

  selector: 'lib-message-center-category-selector',
  imports: [MatSelectModule, MatFormFieldModule, MatButtonModule, MatProgressBar],
  templateUrl: './message-center-category-selector.component.html',
  styleUrl: './message-center-category-selector.component.scss'
})
export class MessageCenterCategorySelectorComponent implements OnInit {
  readonly messageService = inject(MessageCenterService);

  @Input() accountId?: string = '';
  @Input() programPartnerTypeId: number = 0;

  @Output() createNewThread = new EventEmitter<CreateTopicPayload>();

  public categories: MessageCenterCategory[] = [];
  public isLoading: boolean = true;
  public subCategories: MessageCenterChildCategory[] = [];
  public selectedCategory: MessageCenterCategory | null = null;
  public selectedSubcategory: MessageCenterChildCategory | null = null;

  // Special NA object for categories without subcategories
  public readonly NA_SUBCATEGORY: MessageCenterChildCategory = {
    id: null,
    name: 'NA',
    description: '',
    ordinal: 0
  };

  ngOnInit(): void {
    this.isLoading = true;
    this.getTopics();
  }

  categoryChange() {
    try {
      this.subCategories = this.selectedCategory?.childCategories ?? [];
    } catch {
      this.subCategories = [];
    }

    // Set NA_SUBCATEGORY when there are no children, otherwise reset
    if (this.subCategories.length === 0) {
      this.selectedSubcategory = this.NA_SUBCATEGORY;
    } else {
      this.selectedSubcategory = null;
    }
  }

  private getTopics() {
    this.messageService.GetMessageTopicsForProgramPartnerType(this.programPartnerTypeId).subscribe({
      next: (res) => {
        this.categories = res;
        this.categoryChange();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading message topics:', error);
        this.isLoading = false;
      }
    });
  }

  public handleNewThread(): void {
    // Don't include NA_SUBCATEGORY in the payload
    if (this.selectedCategory === null) {
      return;
    }

    const isNaSubcategory = this.selectedSubcategory === this.NA_SUBCATEGORY;
    const newThread: CreateTopicPayload = {
      parentCategoryId: this.selectedCategory.id,
      parentCategoryName: this.selectedCategory?.name,
      childCategoryId: isNaSubcategory ? null : (this.selectedSubcategory?.id ?? null),
      childCategoryName: isNaSubcategory ? '' : (this.selectedSubcategory?.name ?? ''),
      Description: isNaSubcategory
        ? this.selectedCategory.description
        : (this.selectedSubcategory?.description ?? this.selectedCategory.description),
      participantId: this.accountId ?? ''
    };
    this.createNewThread.emit(newThread);
  }

  public get isCategorySelectionValid(): boolean {
    return this.selectedCategory !== null && (this.subCategories.length > 0 ? this.selectedSubcategory !== null : true);
  }
}
